import { useMemo, useState } from "react";
import { Building2, ChevronDown, MapPin } from "lucide-react";
import type { MetaLocation } from "@mtform/shared";
import { useMetaLocations } from "@/hooks/useMetaDirectory";
import { cn } from "@/lib/utils";

// =============================================================================
// LocationsPicker
//
// Pill-shaped trigger that opens a popover with the admin-managed
// locations. Hierarchical: campuses are headers, the buildings /
// entrances under each campus are nested below. Picking a row hands a
// flat object back to the parent so it can fill address, city/zip,
// lat/lon, and pickup_location at once.
//
// `mode="pickup"` only shows locations with isPickup=true; "dropoff"
// only those with isDropoff=true. The component renders nothing when
// the resulting list is empty so the form stays clean before the
// admin has populated the directory.
// =============================================================================

export interface LocationsPickerSelection {
  location: MetaLocation;
  /** Convenience: the rendered "Building Name (West Entrance)" label. */
  label: string;
  /** Best-effort full street address — used to fill the address field. */
  address: string;
}

interface LocationsPickerProps {
  mode: "pickup" | "dropoff";
  onSelect: (picked: LocationsPickerSelection) => void;
  className?: string;
}

export function LocationsPicker({ mode, onSelect, className }: LocationsPickerProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useMetaLocations();

  const grouped = useMemo(() => {
    const all = data?.locations ?? [];
    const filtered = all.filter((l) =>
      mode === "pickup" ? l.isPickup : l.isDropoff,
    );
    if (filtered.length === 0) return null;

    const knownIds = new Set(filtered.map((l) => l.id));
    const roots: MetaLocation[] = [];
    const childrenByParent = new Map<string, MetaLocation[]>();
    for (const l of filtered) {
      if (l.parentLocationId && knownIds.has(l.parentLocationId)) {
        const list = childrenByParent.get(l.parentLocationId) ?? [];
        list.push(l);
        childrenByParent.set(l.parentLocationId, list);
      } else {
        roots.push(l);
      }
    }
    return {
      roots,
      childrenOf: (id: string) => childrenByParent.get(id) ?? [],
    };
  }, [data, mode]);

  if (!grouped) {
    if (isLoading) return null;
    return null;
  }

  const total =
    grouped.roots.reduce(
      (acc, r) => acc + 1 + grouped.childrenOf(r.id).length,
      0,
    ) ?? 0;

  function handlePick(loc: MetaLocation) {
    onSelect({
      location: loc,
      label: formatLocationLabel(loc),
      address: formatLocationAddress(loc),
    });
    setOpen(false);
  }

  return (
    <div className={cn("relative inline-flex", className)}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1.5 rounded-full border border-primary/45 bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary transition hover:bg-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MapPin className="h-3 w-3" strokeWidth={2.5} />
        <span>
          Mount Sinai locations
          <span className="ml-1 rounded-full bg-primary/30 px-1.5 text-[0.65rem] font-bold text-primary-foreground/90">
            {total}
          </span>
        </span>
        <ChevronDown
          className={cn("h-3 w-3 transition", open && "rotate-180")}
          strokeWidth={2.5}
        />
      </button>

      {open ? (
        <>
          <div
            role="presentation"
            className="fixed inset-0 z-40"
            onClick={() => setOpen(false)}
          />
          <div
            role="listbox"
            className="absolute left-0 top-full z-50 mt-1.5 w-[min(24rem,calc(100vw-2rem))] overflow-hidden rounded-xl border border-border bg-popover shadow-2xl"
          >
            <p className="border-b border-border px-3 py-2 text-[0.7rem] text-muted-foreground">
              Pick a {mode === "pickup" ? "pick-up" : "drop-off"} location to fill the
              address fields.
            </p>
            <ul className="max-h-80 overflow-y-auto py-1">
              {grouped.roots.map((root) => {
                const children = grouped.childrenOf(root.id);
                return (
                  <li key={root.id} className="py-0.5">
                    <button
                      type="button"
                      onClick={() => handlePick(root)}
                      className="flex w-full items-start gap-2 px-3 py-2 text-left transition hover:bg-foreground/[0.06]"
                    >
                      <Building2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
                      <span className="flex flex-col">
                        <span className="text-sm font-bold text-foreground">
                          {root.name}
                        </span>
                        {formatLocationAddress(root) ? (
                          <span className="text-xs text-muted-foreground">
                            {formatLocationAddress(root)}
                          </span>
                        ) : null}
                      </span>
                    </button>
                    {children.length > 0 ? (
                      <ul className="mb-1 ml-7 border-l border-foreground/10 pl-2">
                        {children.map((child) => (
                          <li key={child.id}>
                            <button
                              type="button"
                              onClick={() => handlePick(child)}
                              className="flex w-full items-start gap-2 px-3 py-1.5 text-left transition hover:bg-foreground/[0.06]"
                            >
                              <MapPin className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-muted-foreground" />
                              <span className="flex flex-col">
                                <span className="text-sm font-semibold text-foreground">
                                  {child.name}
                                </span>
                                {child.entranceLabel ? (
                                  <span className="text-xs text-muted-foreground">
                                    Entrance: {child.entranceLabel}
                                  </span>
                                ) : null}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function formatLocationLabel(loc: MetaLocation): string {
  if (loc.entranceLabel) return `${loc.name} (${loc.entranceLabel})`;
  return loc.name;
}

export function formatLocationAddress(loc: MetaLocation): string {
  const a = String(loc.addressLine ?? "").trim();
  const c = String(loc.cityZip ?? "").trim();
  if (!a) return c;
  if (!c) return a;
  // Defensive against a mis-entered location row that carries the street in
  // BOTH fields (e.g. addressLine "4300 Alton Rd" + cityZip "4300 Alton Rd,
  // Miami Beach") — emit "4300 Alton Rd, Miami Beach", never the doubled
  // "4300 Alton Rd, 4300 Alton Rd, Miami Beach". The real fix is to correct
  // the row in admin; this keeps the rendered/booked address clean regardless.
  if (c.toLowerCase().startsWith(a.toLowerCase())) return c;
  if (a.toLowerCase().includes(c.toLowerCase())) return a;
  return `${a}, ${c}`;
}
